import { useQuery, UseQueryResult, keepPreviousData } from '@tanstack/react-query';
import { useAppContext } from '../../contexts/Storage';
import { apiClient } from '../../utils/api';
import { UrlBuilder } from '../../utils/urls';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreditBalanceApiResponse {
  balance: number;
}

export interface CreditTransaction {
  id: number;
  name: string;
  amount: number;
  type: string;
  created_at: string;
  user_id: number;
  description: string;
}

export interface CreditTransactionsApiResponse {
  transactions: CreditTransaction[];
  current_page: number;
  total_pages: number;
}

export interface CreditTransactionsFilter {
  id?: number;
  type?: string;
}

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const CREDIT_BALANCE_QUERY_KEY = ['credit-balance'] as const;
export const CREDIT_TRANSACTIONS_QUERY_KEY = ['credit-transactions'] as const;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export interface UseGetCreditBalanceParams {
  userId?: number;
  enabled?: boolean;
}

/**
 * Fetches user's credit balance.
 * GET /api/credits/balance/{user_id}
 */
export function useGetCreditBalance({
  userId,
  enabled = true,
}: UseGetCreditBalanceParams = {}): UseQueryResult<number, Error> {
  const { token, user } = useAppContext();

  const effectiveUserId = userId ?? user?.id;

  return useQuery({
    queryKey: [...CREDIT_BALANCE_QUERY_KEY, effectiveUserId],
    queryFn: async () => {
      if (!token || !effectiveUserId) {
        throw new Error('User not authenticated');
      }

      const response = await apiClient.getWithToken<CreditBalanceApiResponse>(
        UrlBuilder.credits.balance(effectiveUserId),
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch credit balance');
      }

      return response.data?.balance ?? 0;
    },
    enabled: enabled && !!token && !!effectiveUserId,
  });
}

// ---------------------------------------------------------------------------

export interface UseGetCreditTransactionsParams {
  userId?: number;
  filters?: CreditTransactionsFilter;
  enabled?: boolean;
}

/**
 * Fetches user's credit transaction history.
 * POST /api/credits/find/{user_id}
 */
export function useGetCreditTransactions({
  userId,
  filters,
  enabled = true,
}: UseGetCreditTransactionsParams = {}): UseQueryResult<CreditTransactionsApiResponse, Error> {
  const { token, user } = useAppContext();

  const effectiveUserId = userId ?? user?.id;

  return useQuery({
    queryKey: [...CREDIT_TRANSACTIONS_QUERY_KEY, effectiveUserId, filters],
    queryFn: async () => {
      if (!token || !effectiveUserId) {
        throw new Error('User not authenticated');
      }

      const requestBody: Record<string, unknown> = {};
      if (filters?.id !== undefined) requestBody.id = filters.id;
      if (filters?.type) requestBody.type = filters.type;

      const response = await apiClient.postWithToken<CreditTransactionsApiResponse>(
        UrlBuilder.credits.find(effectiveUserId),
        token,
        requestBody
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch credit transactions');
      }

      return response.data as CreditTransactionsApiResponse;
    },
    enabled: enabled && !!token && !!effectiveUserId,
    placeholderData: keepPreviousData,
  });
}
